import React, { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useServiceAutoReplies,
  useUpdateServiceAutoReply,
} from "@/hooks/useServiceAutoReplies";
import type { ServiceAutoReplySettings } from "@/types/serviceAutoReply";

const AUTO_REPLY_TEXT_MAX_LENGTH = 2000;
const EMPTY_SETTINGS: ServiceAutoReplySettings[] = [];

interface ServiceAutoRepliesSettingsProps {
  hotelId: string;
}

interface ServiceAutoReplyFormData {
  requestAcceptedAutoReplyEnabled: boolean;
  requestAcceptedAutoReplyText: string;
  requestCompletedAutoReplyEnabled: boolean;
  requestCompletedAutoReplyText: string;
}

const EMPTY_FORM_VALUES: ServiceAutoReplyFormData = {
  requestAcceptedAutoReplyEnabled: false,
  requestAcceptedAutoReplyText: "",
  requestCompletedAutoReplyEnabled: false,
  requestCompletedAutoReplyText: "",
};

function createServiceAutoReplySchema(t: TFunction) {
  return z
    .object({
      requestAcceptedAutoReplyEnabled: z.boolean(),
      requestAcceptedAutoReplyText: z.string().max(
        AUTO_REPLY_TEXT_MAX_LENGTH,
        t("hotels.aiSettings.serviceAutoReplies.validation.maxLength", {
          defaultValue: "The message cannot exceed 2,000 characters.",
        }),
      ),
      requestCompletedAutoReplyEnabled: z.boolean(),
      requestCompletedAutoReplyText: z.string().max(
        AUTO_REPLY_TEXT_MAX_LENGTH,
        t("hotels.aiSettings.serviceAutoReplies.validation.maxLength", {
          defaultValue: "The message cannot exceed 2,000 characters.",
        }),
      ),
    })
    .superRefine((data, context) => {
      if (
        data.requestAcceptedAutoReplyEnabled &&
        !data.requestAcceptedAutoReplyText.trim()
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["requestAcceptedAutoReplyText"],
          message: t(
            "hotels.aiSettings.serviceAutoReplies.validation.messageRequired",
            {
              defaultValue: "Enter a message before enabling this auto-reply.",
            },
          ),
        });
      }

      if (
        data.requestCompletedAutoReplyEnabled &&
        !data.requestCompletedAutoReplyText.trim()
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["requestCompletedAutoReplyText"],
          message: t(
            "hotels.aiSettings.serviceAutoReplies.validation.messageRequired",
            {
              defaultValue: "Enter a message before enabling this auto-reply.",
            },
          ),
        });
      }
    });
}

function mapSettingsToForm(
  settings: ServiceAutoReplySettings,
): ServiceAutoReplyFormData {
  return {
    requestAcceptedAutoReplyEnabled: settings.requestAcceptedAutoReplyEnabled,
    requestAcceptedAutoReplyText: settings.requestAcceptedAutoReplyText ?? "",
    requestCompletedAutoReplyEnabled: settings.requestCompletedAutoReplyEnabled,
    requestCompletedAutoReplyText: settings.requestCompletedAutoReplyText ?? "",
  };
}

export function ServiceAutoRepliesSettings({
  hotelId,
}: ServiceAutoRepliesSettingsProps): React.JSX.Element {
  const { t } = useTranslation();
  const schema = useMemo(() => createServiceAutoReplySchema(t), [t]);
  const settingsQuery = useServiceAutoReplies(hotelId);
  const updateSettings = useUpdateServiceAutoReply(hotelId);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const settings = settingsQuery.data ?? EMPTY_SETTINGS;
  const selectedSettings = useMemo(
    () => settings.find((item) => item.serviceId === selectedServiceId) ?? null,
    [selectedServiceId, settings],
  );

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<ServiceAutoReplyFormData>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM_VALUES,
    mode: "onChange",
  });

  const acceptedEnabled = watch("requestAcceptedAutoReplyEnabled");
  const completedEnabled = watch("requestCompletedAutoReplyEnabled");

  useEffect(() => {
    if (
      settings.length > 0 &&
      !settings.some((item) => item.serviceId === selectedServiceId)
    ) {
      setSelectedServiceId(settings[0].serviceId);
    }
  }, [selectedServiceId, settings]);

  useEffect(() => {
    reset(
      selectedSettings
        ? mapSettingsToForm(selectedSettings)
        : EMPTY_FORM_VALUES,
    );
  }, [reset, selectedSettings]);

  const submitSettings = handleSubmit(async (data) => {
    if (!selectedServiceId) {
      return;
    }

    try {
      const updated = await updateSettings.mutateAsync({
        serviceId: selectedServiceId,
        data: {
          requestAcceptedAutoReplyEnabled: data.requestAcceptedAutoReplyEnabled,
          requestAcceptedAutoReplyText:
            data.requestAcceptedAutoReplyText.trim() || null,
          requestCompletedAutoReplyEnabled:
            data.requestCompletedAutoReplyEnabled,
          requestCompletedAutoReplyText:
            data.requestCompletedAutoReplyText.trim() || null,
        },
      });
      reset(mapSettingsToForm(updated));
      toast.success(
        t("hotels.aiSettings.serviceAutoReplies.saveSuccess", {
          defaultValue: "Service auto-replies saved",
        }),
      );
    } catch {
      toast.error(
        t("hotels.aiSettings.serviceAutoReplies.saveError", {
          defaultValue: "Could not save service auto-replies",
        }),
      );
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("hotels.aiSettings.serviceAutoReplies.title", {
            defaultValue: "Service auto-replies",
          })}
        </CardTitle>
        <CardDescription>
          {t("hotels.aiSettings.serviceAutoReplies.description", {
            defaultValue:
              "Choose the messages Guesteria sends to the guest chat as a service request moves through its lifecycle.",
          })}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {settingsQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">
            {t("hotels.aiSettings.serviceAutoReplies.loading", {
              defaultValue: "Loading services...",
            })}
          </p>
        ) : null}

        {settingsQuery.isError ? (
          <p className="text-destructive text-sm">
            {t("hotels.aiSettings.serviceAutoReplies.loadError", {
              defaultValue: "Could not load service auto-replies.",
            })}
          </p>
        ) : null}

        {!settingsQuery.isLoading &&
        !settingsQuery.isError &&
        settings.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("hotels.aiSettings.serviceAutoReplies.empty", {
              defaultValue: "No services are available for AI auto-replies.",
            })}
          </p>
        ) : null}

        {settings.length > 0 ? (
          <form className="flex flex-col gap-5" onSubmit={submitSettings}>
            <div className="flex flex-col gap-2">
              <Label htmlFor="service-auto-reply-service">
                {t("hotels.aiSettings.serviceAutoReplies.serviceLabel", {
                  defaultValue: "Service",
                })}
              </Label>
              <Select
                value={selectedServiceId}
                onValueChange={setSelectedServiceId}
                disabled={updateSettings.isPending}
              >
                <SelectTrigger
                  id="service-auto-reply-service"
                  className="w-full"
                >
                  <SelectValue
                    placeholder={t(
                      "hotels.aiSettings.serviceAutoReplies.servicePlaceholder",
                      { defaultValue: "Choose a service" },
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {settings.map((item) => (
                      <SelectItem key={item.serviceId} value={item.serviceId}>
                        {item.serviceName}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <Controller
                    control={control}
                    name="requestAcceptedAutoReplyEnabled"
                    render={({ field }) => (
                      <Checkbox
                        id="request-accepted-auto-reply"
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="request-accepted-auto-reply">
                      {t(
                        "hotels.aiSettings.serviceAutoReplies.accepted.title",
                        {
                          defaultValue:
                            "When the service request is approved or in progress",
                        },
                      )}
                    </Label>
                    <CardDescription>
                      {t(
                        "hotels.aiSettings.serviceAutoReplies.accepted.description",
                        {
                          defaultValue:
                            "The message is sent once, on the first approved or in-progress status.",
                        },
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  aria-invalid={Boolean(errors.requestAcceptedAutoReplyText)}
                  disabled={!acceptedEnabled}
                  maxLength={AUTO_REPLY_TEXT_MAX_LENGTH}
                  placeholder={t(
                    "hotels.aiSettings.serviceAutoReplies.accepted.placeholder",
                    {
                      defaultValue:
                        "Your request has been accepted and is now in progress.",
                    },
                  )}
                  {...register("requestAcceptedAutoReplyText")}
                />
                {errors.requestAcceptedAutoReplyText?.message ? (
                  <p className="text-destructive mt-2 text-sm">
                    {errors.requestAcceptedAutoReplyText.message}
                  </p>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <Controller
                    control={control}
                    name="requestCompletedAutoReplyEnabled"
                    render={({ field }) => (
                      <Checkbox
                        id="request-completed-auto-reply"
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="request-completed-auto-reply">
                      {t(
                        "hotels.aiSettings.serviceAutoReplies.completed.title",
                        {
                          defaultValue:
                            "When the service is delivered and the request is closed",
                        },
                      )}
                    </Label>
                    <CardDescription>
                      {t(
                        "hotels.aiSettings.serviceAutoReplies.completed.description",
                        {
                          defaultValue:
                            "The message is sent once, on the first completed or done status.",
                        },
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  aria-invalid={Boolean(errors.requestCompletedAutoReplyText)}
                  disabled={!completedEnabled}
                  maxLength={AUTO_REPLY_TEXT_MAX_LENGTH}
                  placeholder={t(
                    "hotels.aiSettings.serviceAutoReplies.completed.placeholder",
                    {
                      defaultValue:
                        "Your request has been completed. We hope you enjoy it!",
                    },
                  )}
                  {...register("requestCompletedAutoReplyText")}
                />
                {errors.requestCompletedAutoReplyText?.message ? (
                  <p className="text-destructive mt-2 text-sm">
                    {errors.requestCompletedAutoReplyText.message}
                  </p>
                ) : null}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="new_default"
                disabled={
                  updateSettings.isPending || !selectedServiceId || !isValid
                }
              >
                <Save />
                {updateSettings.isPending
                  ? t("hotels.aiSettings.serviceAutoReplies.saving", {
                      defaultValue: "Saving...",
                    })
                  : t("hotels.aiSettings.serviceAutoReplies.save", {
                      defaultValue: "Save auto-replies",
                    })}
              </Button>
            </div>
          </form>
        ) : null}
      </CardContent>
    </Card>
  );
}
